import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppSettings, defaults } from '../config';
import { OpenApiException } from '../types/responses/OpenApiException';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  get notify(): Observable<Record<string, any>> {
    return this.notify$.asObservable();
  }

  private htmlElement!: HTMLElement;

  private notify$ = new BehaviorSubject<Record<string, any>>({});

  constructor(
    private toastr: ToastrService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.htmlElement = this.document.documentElement;
  }

  getOptions() {
    return this.options;
  }

  setOptions(options: AppSettings) {
    this.options = Object.assign(defaults, options);
    this.notify$.next(this.options);
  }

  toggleTheme(): void {
    this.options.theme = this.options.theme === 'dark' ? 'light' : 'dark';
    if (this.options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
    this.notify$.next(this.options);
  }

  private options = defaults;

  getLanguage() {
    return this.options.language;
  }

  delayResolve<T>(data: T): Promise<T> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(data);
      }, environment.delayForMockResolveReject);
    });
  }

  delayReject<T>(error: Error): Promise<T> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        reject(error);
      }, environment.delayForMockResolveReject);
    });
  }

  onSuccessToaster(title: string, content: string, sticky: boolean = false) {
    this.toastr.success(content, title, {
      positionClass: 'toast-top-right',
      timeOut: 6000,
      closeButton: true,
      disableTimeOut: sticky,
    });
  }

  onFailureToaster(title: string, content: any) {
    if (this.isOpenApiException(content)) {
      this.toastr.error(content.userMessage, title, {
        positionClass: 'toast-bottom-center',
        timeOut: 6000,
      });
      console.log('error in ' + title, content);
      this.pushServiceLayerErrorsToGoogleAnalytics(title, content);
    } else {
      console.error('Unexpected error:', content);

      if (typeof content === 'string') {
        this.toastr.error(content, title, {
          positionClass: 'toast-bottom-center',
          timeOut: 6000,
        });
      } else {
        this.toastr.error(
          'An unexpected error occurred',
          'Unexpected error occurred',
          {
            positionClass: 'toast-bottom-center',
            timeOut: 6000,
          },
        );
      }
    }
  }

  private pushServiceLayerErrorsToGoogleAnalytics(
    title: string,
    content: OpenApiException,
  ) {
    if (window && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'exception', {
        event_category: 'Service Layer Error ' + title,
        event_label: content.userMessage + ' ' + content.apiErrorMessage,
        value: 1,
        non_interaction: true,
      });
    } else {
      console.warn('gtag is not defined');
    }
  }

  private isOpenApiException(obj: any): obj is OpenApiException {
    return (
      obj &&
      typeof obj === 'object' &&
      'userMessage' in obj &&
      'apiErrorMessage' in obj
    );
  }

  setLanguage(lang: string) {
    this.options.language = lang;
    this.notify$.next({ lang });
  }
}
